import { Spacing } from '@persona-id/design-tokens';
import { lowerCase, startCase } from 'lodash/fp';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import { SectionDescription } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import ContextMenu from 'dashboard/components/ContextMenu';
import { SUPPORTED_FIELD_TYPE } from 'dashboard/components/DocumentFields/util';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import useLearnMoreEventClick from 'dashboard/components/ProductUpsellTooltip/useLearnMoreEventClick';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Label, Paragraph } from 'design-system/components/Text';
import { FIELD_SCHEMA_TYPE_DISPLAY, FieldSchema, FieldSchemaType } from 'lib/fields';

const TableContainer = styled.div`
  max-height: 512px;
  overflow-y: auto;
`;

const EmptyTable = ({ colSpan, children }: { colSpan: number; children: React.ReactNode }) => (
  <TableRow style={{ height: '80px' }}>
    <TableCell colSpan={colSpan}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    </TableCell>
  </TableRow>
);

const LearnMoreTable = ({
  id,
  accessTier,
  colSpan,
}: {
  id: string;
  accessTier: Exclude<EditorAccessTier, EditorAccessTier.Any>;
  colSpan: number;
}) => {
  const { linkElementId, onLearnMoreClick } = useLearnMoreEventClick({ id });

  return (
    <EmptyTable colSpan={colSpan}>
      <Label weight='bold'>This feature is not available on your plan</Label>
      <Spacer height={0.5} />
      <a
        href='#'
        target='_blank'
        id={linkElementId}
        rel='noreferrer'
        onClick={(e) => {
          e.preventDefault();
          onLearnMoreClick({ accessTier });
        }}
        style={{ textDecoration: 'underline' }}
      >
        Learn more
      </a>
    </EmptyTable>
  );
};

const ActivableTableRow = styled(TableRow)`
  background-color: ${({ theme, active }) => active && theme.color.background.highlight};
`;

const FilterContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing[2]};
`;

interface DocumentFieldsProps {
  documentFields: FieldSchema[];
  selected: number | null;
  onSelect: (index?: number | null) => void;
  onDelete: (index: number) => void;
  editable: boolean;
}

const DocumentFields = ({
  documentFields,
  selected,
  onSelect,
  onDelete,
  editable,
}: DocumentFieldsProps) => {
  const hasAccessForEnhancedTier = useHasAccessForTierDeterminator()(
    EditorAccessTier.InquiriesEnhanced,
  );

  const [searchString, setSearchString] = useState('');
  const [typeFilters, setTypeFilters] = useState<string[]>([]);

  const filteredDocumentFields = documentFields
    .map((documentField, index) => [documentField, index] as const)
    .filter(
      ([documentField]) =>
        lowerCase(documentField.label).includes(lowerCase(searchString)) ||
        lowerCase(startCase(documentField.key)).includes(lowerCase(searchString)),
    )
    .filter(
      ([documentField]) => typeFilters.length === 0 || typeFilters.includes(documentField.type),
    );

  useEffect(() => setSearchString(''), [documentFields]);

  const typeFilterOptions = SUPPORTED_FIELD_TYPE.map((fieldType) => ({
    label: FIELD_SCHEMA_TYPE_DISPLAY[fieldType] ?? '',
    value: fieldType,
  }))
    .filter(({ label }) => label !== '')
    .sort(({ label: a }, { label: b }) => a.localeCompare(b));

  const typeFilterLabelTextMapper = useLabelTextMapper(typeFilterOptions, 'types', {
    emptyIsAll: true,
  });

  const handleDelete = (index: number) => {
    onDelete(index);
    if (selected === index) {
      onSelect(undefined);
    } else if (selected != null && selected > index) {
      onSelect(selected - 1);
    }
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={
            <Button
              id='document_fields__add-button'
              variant='basic'
              size='sm'
              onClick={() => onSelect(null)}
              disabled={!editable || !hasAccessForEnhancedTier}
            >
              Add document field
            </Button>
          }
        >
          Document Fields
          {!hasAccessForEnhancedTier && (
            <ProductUpsellTooltip
              accessTier={EditorAccessTier.InquiriesEnhanced}
              learnMoreId='verifications_document_document_fields'
              trigger={
                <div>
                  <InquiriesGatingSticker stickerType={EnhancedGatingStickerType.Limited} />
                </div>
              }
            />
          )}
        </Card.Title>
        <SectionDescription>
          Document Fields are used to reference Extractions and Insights across other Persona
          products like Workflows and Cases.
        </SectionDescription>
        <FilterContainer>
          <SearchInput
            title={null}
            placeholder='Search document fields'
            value={searchString}
            handleInput={setSearchString}
            disabled={!editable || !hasAccessForEnhancedTier}
          />
          <MultiSelect
            value={typeFilters}
            selectOptionValues={typeFilterOptions}
            labelTextMapper={typeFilterLabelTextMapper}
            onChange={setTypeFilters}
            searchEnabled
            disabled={!editable || !hasAccessForEnhancedTier}
          />
        </FilterContainer>
        <Spacer height={0.5} />
        <TableContainer>
          <Table>
            <TableHead sticky>
              <TableRow>
                <TableHeaderCell>Label & key</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell width='min' />
              </TableRow>
            </TableHead>
            <TableBody>
              {!hasAccessForEnhancedTier && documentFields.length === 0 ? (
                <LearnMoreTable
                  id='verifications_document_document_fields'
                  accessTier={EditorAccessTier.InquiriesEnhanced}
                  colSpan={3}
                />
              ) : documentFields.length === 0 ? (
                <EmptyTable colSpan={3}>No document fields configured</EmptyTable>
              ) : filteredDocumentFields.length === 0 ? (
                <EmptyTable colSpan={3}>No document fields found</EmptyTable>
              ) : (
                filteredDocumentFields.map(([documentField, index]) => (
                  <ActivableTableRow
                    key={index}
                    id={documentField.key}
                    onClick={
                      editable && hasAccessForEnhancedTier ? () => onSelect(index) : undefined
                    }
                    active={index === selected}
                  >
                    <TableCell>
                      {documentField.label}
                      <Paragraph color='tertiary'>{documentField.key}</Paragraph>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Sticker>{FIELD_SCHEMA_TYPE_DISPLAY[documentField.type]}</Sticker>
                        {documentField.type === FieldSchemaType.Array && (
                          <>
                            <Spacer width={0.5} />
                            of
                            <Spacer width={0.5} />
                            <Sticker>
                              {FIELD_SCHEMA_TYPE_DISPLAY[documentField.config['item-schema'].type]}
                            </Sticker>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ContextMenu
                        variant='inverse'
                        disabled={!editable || !hasAccessForEnhancedTier}
                        items={[
                          {
                            id: 'document-fields__context-menu-delete',
                            label: 'Delete',
                            onClick: () => handleDelete(index),
                            variant: 'danger',
                          },
                        ]}
                        renderItem={(item) => (
                          <ContextMenu.Item {...item}>{item.label}</ContextMenu.Item>
                        )}
                      />
                    </TableCell>
                  </ActivableTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card.Body>
    </Card>
  );
};

export default DocumentFields;
